"use client";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "antd";
import Helper from "../../util/helper";
import formatMsg from "../../util/formatMsg";
import { notification } from "../../util/notification";
import { createCheckoutSession, getPaymentMethods, sliceOperation } from "../../redux/payment/reducer";

// Capture a reusable autopay card on Stripe's OWN hosted page — Bookingo never sees or stores
// card data. We open a Checkout Session (mode:setup) in THIS tab and let Stripe return the payer
// here, rather than sending them to a second tab they then have to find their way back from.
//
// Returning in the same tab means the page remounts, so the "which card is new" baseline is
// carried across the round trip in sessionStorage rather than component state. The baseline is
// each saved card's updatedOn, not just its id: re-entering a card the family already has is
// folded onto the existing record (same Stripe fingerprint) rather than saved twice, so no new id
// ever appears — but that record's updatedOn moves, and that is the card they just entered. On the way back we
// poll the vault briefly — the card is only ours once the setup_intent.succeeded webhook has
// vaulted it, which lands a beat after Stripe redirects — then fire onSaved(paymentMethodId) so
// the booking's autopay gate can enrol.
//
// Drop-in replacement for <AddPaymentMethod> (same { accountId, onSaved } contract).

const RETURN_PARAM = "cardSetup";      // ?cardSetup=done | cancelled, added to the Stripe return url
const STORE_KEY = "bookingo.cardSetup"; // { accountId, baseline: string[] }
const POLL_MS = 2000;
const MAX_POLLS = 15;                   // ~30s after returning, then offer a manual check
const START_WATCHDOG_MS = 15000;        // if the hosted URL never arrives, re-enable the button
const RESUME_WINDOW_MS = 30 * 60 * 1000; // a dialog reopened this soon after returning keeps checking

const urlWith = (value: string) => {
  if (typeof window === "undefined") return "";
  const u = new URL(window.location.href);
  u.searchParams.set(RETURN_PARAM, value);
  return u.toString();
};

const stripReturnParam = () => {
  if (typeof window === "undefined") return;
  const u = new URL(window.location.href);
  if (!u.searchParams.has(RETURN_PARAM)) return;
  u.searchParams.delete(RETURN_PARAM);
  window.history.replaceState({}, "", u.toString());
};

// Which saved card did the payer just enter? A card we had never seen, else a known one Stripe
// refreshed while they were away (re-entered, so folded onto the existing record) — the most
// recently refreshed if several. Both sides are server timestamps, so the device clock never counts.
export const findEnteredCard = (methods: any[], before: Map<string, number>) => {
  const brandNew = methods.find((m: any) => m.id && !before.has(m.id));
  if (brandNew) return brandNew;
  return methods
    .filter((m: any) => m.id && (Number(m.updatedOn) || 0) > (before.get(m.id) || 0))
    .sort((a: any, b: any) => (Number(b.updatedOn) || 0) - (Number(a.updatedOn) || 0))[0] || null;
};

const readStore = () => {
  try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || "null"); } catch { return null; }
};

// Stripe returns the payer to a freshly loaded page, so whatever dialog this component sat in is
// closed and nothing would receive onSaved. The caller stores what the payer was doing as
// `resumeIntent` when they leave; on the way back it reads it here and reopens that dialog, which
// mounts this component again to finish the job. Read-only: this component clears it when done.
export const getPendingCardSetup = (accountId?: string): { outcome: string; intent: any } | null => {
  if (typeof window === "undefined") return null;
  const outcome = new URL(window.location.href).searchParams.get(RETURN_PARAM);
  if (!outcome) return null;
  const stored = readStore();
  if (!stored || (accountId && stored.accountId !== accountId)) return null;
  return { outcome, intent: stored.intent || null };
};
const writeStore = (value: any) => { try { sessionStorage.setItem(STORE_KEY, JSON.stringify(value)); } catch { /* private mode */ } };
// Same fix? Only a dialog for the same subscription / booking may pick up a check left running.
const sameIntent = (a: any, b: any) =>
  (a?.kind ?? null) === (b?.kind ?? null) && (a?.subscriptionId ?? null) === (b?.subscriptionId ?? null) && (a?.bookingId ?? null) === (b?.bookingId ?? null);
const clearStore = () => { try { sessionStorage.removeItem(STORE_KEY); } catch { /* private mode */ } };

// `bindTo` ({ subscriptionIds?, bookingIds? }) names the subscriptions this card is being added for.
// Stripe's webhook then binds the new card to those only — never to the family's other bookings.
// An empty list binds nothing (the caller binds the card itself once it lands).
export default function HostedCardSetup({ accountId, onSaved, size = "small", resumeIntent, bindTo }: any) {
  const dispatch = useDispatch();
  const { paymentReducer, loggedInData, firebase }: any = Helper.getSelectorState(["paymentReducer"]);
  const authData = loggedInData || firebase;
  const sessionUrl = paymentReducer?.checkoutSessionUrl;
  const methods = paymentReducer?.paymentMethods || [];
  // The baseline is only as good as the list it is taken from. Screens that never loaded the saved
  // cards (the booking cards on My Bookings) would otherwise record an empty one, and on return
  // every existing card — the failing one included — would look like the one just entered.
  const methodsLoaded = paymentReducer?.paymentMethodsAccountId === accountId;

  const [phase, setPhase] = useState<"idle" | "starting" | "returning" | "stalled">("idle");
  const baselineRef = useRef<Map<string, number>>(new Map());
  const pollRef = useRef<any>(null);
  const pollCountRef = useRef(0);
  const watchdogRef = useRef<any>(null);
  // "I've added my card": a check the payer asked for, and whether it came back empty.
  const [manualChecking, setManualChecking] = useState(false);
  const [notYet, setNotYet] = useState(false);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => { stopPolling(); if (watchdogRef.current) clearTimeout(watchdogRef.current); }, []);

  // Watch the saved cards for the one the payer enters on Stripe, against the baseline taken when
  // they left (which cards were on file, and when each was last updated).
  const beginChecking = (stored: any) => {
    // { id: updatedOn }; an array is the older shape (ids only) from a session already in flight.
    const baseline = stored.baseline || {};
    baselineRef.current = Array.isArray(baseline)
      ? new Map(baseline.map((id: string) => [id, 0]))
      : new Map(Object.entries(baseline).map(([id, at]) => [id, Number(at) || 0]));
    pollCountRef.current = 0;
    setPhase("returning");
    dispatch(getPaymentMethods({ loggedInData: authData, accountId }));
    stopPolling();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current > MAX_POLLS) { stopPolling(); setPhase("stalled"); return; }
      dispatch(getPaymentMethods({ loggedInData: authData, accountId }));
    }, POLL_MS);
  };

  useEffect(() => {
    if (typeof window === "undefined" || !accountId) return;
    const outcome = new URL(window.location.href).searchParams.get(RETURN_PARAM);
    const stored = readStore();
    // Coming back from Stripe in this same tab: the param says how it ended.
    if (outcome) {
      stripReturnParam();
      if (outcome !== "done" || !stored || stored.accountId !== accountId) { clearStore(); return; }
      writeStore({ ...stored, returnedAt: Date.now() });
      beginChecking(stored);
      return;
    }
    // The dialog was closed while still checking and opened again for the same fix: carry on
    // rather than offering "Add card" again as if nothing had happened.
    if (stored?.accountId === accountId && stored.returnedAt && Date.now() - stored.returnedAt < RESUME_WINDOW_MS
      && sameIntent(stored.intent, resumeIntent)) {
      beginChecking(stored);
      return;
    }
    // Otherwise a fresh list of saved cards, so the baseline taken on click is current.
    dispatch(getPaymentMethods({ loggedInData: authData, accountId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const start = () => {
    // Remember what was on file BEFORE leaving; component state does not survive the redirect.
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        accountId,
        baseline: Object.fromEntries(methods.filter((m: any) => m.id).map((m: any) => [m.id, Number(m.updatedOn) || 0])),
        intent: resumeIntent || null,
      }));
    } catch { /* private mode — we fall back to treating any card as the new one */ }
    dispatch(sliceOperation({ checkoutSessionUrl: undefined }));
    dispatch(createCheckoutSession({
      loggedInData: authData,
      accountId,
      bindTo,
      successUrl: urlWith("done"),
      cancelUrl: urlWith("cancelled"),
    }));
    setPhase("starting");
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      // URL never came back (e.g. backend/config error — saga already notified) → re-enable.
      setPhase((p) => (p === "starting" ? "idle" : p));
      clearStore();
    }, START_WATCHDOG_MS);
  };

  // Hosted URL is ready → hand this tab over to Stripe. Stripe returns the payer to the url we
  // gave it, which is this page plus the outcome marker the effect above reads.
  useEffect(() => {
    if (phase !== "starting" || !sessionUrl) return;
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    window.location.assign(sessionUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sessionUrl]);

  // A new card appeared in the vault → hand it back to the booking flow and stop.
  useEffect(() => {
    if (phase !== "returning" && phase !== "stalled") return;
    const fresh = findEnteredCard(methods, baselineRef.current);
    if (manualChecking) {
      setManualChecking(false);
      setNotYet(!fresh);
    }
    if (fresh) {
      stopPolling();
      clearStore();
      setPhase("idle");
      notification("success", formatMsg("card.saved"));
      onSaved && onSaved(fresh.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods]);

  const manualCheck = () => {
    setNotYet(false);
    setManualChecking(true);
    dispatch(getPaymentMethods({ loggedInData: authData, accountId }));
  };

  if (phase === "returning" || phase === "stalled") {
    return (
      <div style={{ padding: "8px 0" }}>
        <div style={{ fontSize: size === "middle" ? 14 : 13, lineHeight: 1.5, color: "#8D8D8D", marginBottom: 10 }}>
          {formatMsg(notYet ? "card.hostedNotYet" : phase === "stalled" ? "card.hostedStalled" : "card.hostedReturning")}
        </div>
        <Button size="small" loading={manualChecking} onClick={manualCheck}>{formatMsg("card.hostedCheck")}</Button>
      </div>
    );
  }

  return (
    <Button size={size} type="primary" ghost loading={phase === "starting" || !methodsLoaded} onClick={start}
      style={size === "middle" ? { height: 40, fontSize: 15, fontWeight: 600, width: "100%" } : undefined}>
      {formatMsg("autopay.addCardSecure")}
    </Button>
  );
}
